import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import EmployeeSummary from "../components/EmployeeSummary";
import FollowUpSection from "../components/intake/FollowUpSection";
import RecordDetailsForm from "../components/intake/RecordDetailsForm";
import SubmitActions from "../components/intake/SubmitActions";
import Form from "../components/form/Form";
import PageSectionCard from "../components/PageSectionCard";
import { useAppSession } from "../context/AppSessionContext";
import {
  CreateEpisodeFormValues,
  toCreateEpisodePayload,
  toCreateFollowUpPayloads,
  toDateTimeLocalValue,
} from "../lib/intakeForm";
import { readErrorMessage } from "../lib/http";
import { CreateEpisodeResponse } from "../types/incapacity";
import { Employee, getEmployeeClientCompany } from "../types/employees";

const defaultValues: CreateEpisodeFormValues = {
  reportedAt: toDateTimeLocalValue(),
  startDate: "",
  estimatedReturnDate: "",
  returnDate: "",
  reasonCode: "",
  verified: false,
  musculoskeletal: false,
  comment: "",
  followUps: [],
};

function CreateEpisodeDetailsPage() {
  const { t } = useTranslation();
  const { authorizedFetch, canMaintainEmployees } = useAppSession();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const employeeId = searchParams.get("employeeId");
  const form = useForm<CreateEpisodeFormValues>({ defaultValues });
  const commentLength = (form.watch("comment") ?? "").length;

  const [employee, setEmployee] = useState<Employee | null>(null);
  const [isLoadingEmployee, setIsLoadingEmployee] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [followUpErrors, setFollowUpErrors] = useState<string[]>([]);
  const [createdEpisodeId, setCreatedEpisodeId] = useState<string | null>(null);

  useEffect(() => {
    if (!employeeId) {
      setEmployee(null);
      setLoadError(
        t("Missing employee ID in URL. Start from the episodes list."),
      );
      setIsLoadingEmployee(false);
      return;
    }

    let cancelled = false;

    const loadEmployee = async () => {
      setIsLoadingEmployee(true);
      setLoadError(null);

      try {
        const response = await authorizedFetch(`/employees/${employeeId}`);

        if (!response.ok) {
          throw new Error(
            await readErrorMessage(
              response,
              response.status === 404
                ? t("Employee not found.")
                : t("Failed to load employee ({{status}}).", {
                    status: response.status,
                  }),
            ),
          );
        }

        const payload = (await response.json()) as Employee;
        if (
          payload.deletedAt ||
          payload.workstation?.deletedAt ||
          getEmployeeClientCompany(payload)?.deletedAt
        ) {
          throw new Error(
            t(
              "This employee is deactivated or belongs to a deactivated company or workstation.",
            ),
          );
        }

        if (!cancelled) {
          setEmployee(payload);
        }
      } catch (error) {
        if (cancelled) {
          return;
        }

        const message =
          error instanceof Error
            ? error.message
            : t("Failed to load employee.");
        setLoadError(message);
        setEmployee(null);
      } finally {
        if (!cancelled) {
          setIsLoadingEmployee(false);
        }
      }
    };

    void loadEmployee();

    return () => {
      cancelled = true;
    };
  }, [authorizedFetch, employeeId, t]);

  const canSubmit = !!employee && canMaintainEmployees && !createdEpisodeId;

  const onSubmit = async (values: CreateEpisodeFormValues) => {
    if (!canMaintainEmployees) {
      setSubmitError(
        t("Only secretary/manager/admin users can create and edit episodes."),
      );
      return;
    }

    if (!employee) {
      setSubmitError(t("Select an employee before submitting."));
      return;
    }

    if (createdEpisodeId) {
      setSubmitError(
        t("Episode was already created. Open the created episode to continue."),
      );
      return;
    }

    setSubmitError(null);
    setFollowUpErrors([]);

    try {
      const createEpisodeResponse = await authorizedFetch("/episodes", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(toCreateEpisodePayload(values, employee.id)),
      });

      if (!createEpisodeResponse.ok) {
        const fallbackMessage =
          createEpisodeResponse.status === 401 ||
          createEpisodeResponse.status === 403
            ? t("You are not authorized to create episodes.")
            : createEpisodeResponse.status === 404
              ? t("The selected employee no longer exists.")
              : createEpisodeResponse.status === 400
                ? t("The episode request is invalid.")
                : t("Failed to create episode ({{status}}).", {
                    status: createEpisodeResponse.status,
                  });

        throw new Error(
          await readErrorMessage(createEpisodeResponse, fallbackMessage),
        );
      }

      const payload =
        (await createEpisodeResponse.json()) as CreateEpisodeResponse;
      const episodeId = payload.episode.id;
      const initialComment = values.comment.trim();

      if (initialComment) {
        const commentResponse = await authorizedFetch(
          `/episodes/${episodeId}/comments`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              text: initialComment,
              employeeId: employee.id,
            }),
          },
        );

        if (!commentResponse.ok) {
          setCreatedEpisodeId(episodeId);
          throw new Error(
            await readErrorMessage(
              commentResponse,
              t(
                "Episode created, but failed to create initial comment ({{status}}).",
                {
                  status: commentResponse.status,
                },
              ),
            ),
          );
        }
      }

      const followUps = toCreateFollowUpPayloads(values.followUps);

      if (followUps.length === 0) {
        toast.success(t("Created episode successfully."));
        navigate(`/episodes/${episodeId}`);
        return;
      }

      const followUpResults = await Promise.allSettled(
        followUps.map(async (followUp, index) => {
          const followUpResponse = await authorizedFetch("/follow-ups", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              episodeId,
              ...followUp,
            }),
          });

          if (!followUpResponse.ok) {
            throw new Error(
              await readErrorMessage(
                followUpResponse,
                t("Failed to create follow-up {{index}} ({{status}}).", {
                  index: index + 1,
                  status: followUpResponse.status,
                }),
              ),
            );
          }
        }),
      );

      const failures = followUpResults.reduce<string[]>(
        (messages, result, index) => {
          if (result.status === "fulfilled") {
            return messages;
          }

          const reason =
            result.reason instanceof Error
              ? result.reason.message
              : t("Failed to create follow-up {{index}}.", {
                  index: index + 1,
                });
          messages.push(
            t("Follow-up {{index}}: {{reason}}", { index: index + 1, reason }),
          );
          return messages;
        },
        [],
      );

      if (failures.length > 0) {
        setCreatedEpisodeId(episodeId);
        setSubmitError(
          t("Episode created, but one or more follow-ups failed."),
        );
        setFollowUpErrors(failures);
        return;
      }

      toast.success(t("Created episode successfully."));
      navigate(`/episodes/${episodeId}`);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t("Failed to create episode.");
      setSubmitError(message);
    }
  };

  const helperText = useMemo(() => {
    if (!employee) {
      return t("Load an employee to continue.");
    }

    const context = [
      getEmployeeClientCompany(employee)?.name,
      employee.workstation?.name,
    ].filter((part): part is string => !!part);

    const suffix = context.length > 0 ? ` · ${context.join(" · ")}` : "";

    return `${t("Creating episode for {{name}}", {
      name: employee.fullName,
    })}${suffix}`;
  }, [employee, t]);

  const formId = "episode-intake-form";
  const isSubmitting = form.formState.isSubmitting;

  return (
    <PageSectionCard
      titleId="create-episode-details-title"
      eyebrow={t("Incapacity")}
      title={t("Create incapacity episode")}
      lead={t("Complete episode details and add one or more follow-up tasks.")}
      stickyHeader
      actions={
        <button
          className="primaryButton"
          type="submit"
          form={formId}
          disabled={!canSubmit || isSubmitting}
        >
          {isSubmitting ? t("Creating episode...") : t("Create episode")}
        </button>
      }
    >
      <p className="hintText">{helperText}</p>

      {isLoadingEmployee ? <p>{t("Loading employee...")}</p> : null}

      {loadError ? (
        <p className="errorMessage" role="alert">
          {loadError}
        </p>
      ) : null}

      {employee ? (
        <Form
          id={formId}
          className="intakeFormLayout"
          form={form}
          onSubmit={onSubmit}
        >
          <section
            className="subCard"
            aria-labelledby="intake-employee-heading"
          >
            <h3 id="intake-employee-heading" className="subCardTitle">
              {t("Employee details")}
            </h3>
            <EmployeeSummary employee={employee} showFullName />
          </section>

          <RecordDetailsForm
            form={form}
            stepLabel={t("Episode details")}
            showCommentField
            commentLength={commentLength}
          />

          <FollowUpSection form={form} stepLabel={t("Follow-ups")} />

          {followUpErrors.length > 0 ? (
            <ul className="followUpErrorList" role="alert">
              {followUpErrors.map((errorMessage) => (
                <li key={errorMessage}>{errorMessage}</li>
              ))}
            </ul>
          ) : null}

          <SubmitActions
            canSubmit={canSubmit}
            isSubmitting={form.formState.isSubmitting}
            submitError={submitError}
            onCancel={() => navigate("/intake")}
            lockedEpisodeId={createdEpisodeId}
          />
        </Form>
      ) : null}
    </PageSectionCard>
  );
}

export default CreateEpisodeDetailsPage;
